import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Award, Plus, RefreshCw, Eye, FileText, AlertCircle, Edit, Trash2, Search, Filter, Stethoscope, PlayCircle, UserCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import MedicalNotesPanel from '../../MedicalNotesPanel';
import UpgradePrompt from '@/components/ui/UpgradePrompt';
import AddressInput from '@/components/ui/AddressInput';
import MemberServiceUsageDialog from './MemberServiceUsageDialog';
import MemberDetailDialog from './MemberDetailDialog';

const MembersTab = ({ members, fetchData }) => {
  const [selectedMember, setSelectedMember] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [serviceUsageDialogOpen, setServiceUsageDialogOpen] = useState(false);
  const [memberDetailDialogOpen, setMemberDetailDialogOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [startDate, setStartDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [preview, setPreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [gatewayStatus, setGatewayStatus] = useState(null);
  const [processingGatewayPayment, setProcessingGatewayPayment] = useState(false);
  const [editFormData, setEditFormData] = useState({
    phone: '',
    date_of_birth: '',
    address: '',
    emergency_contact: '',
    emergency_phone: ''
  });
  
  // New Member Form State
  const [newMemberData, setNewMemberData] = useState({
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    plan_id: '',
    send_welcome_email: true
  });
  const [creatingMember, setCreatingMember] = useState(false);
  
  // Advanced Search States
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Upgrade Prompt State
  const [upgradePromptOpen, setUpgradePromptOpen] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  
  // Patient Impersonation State
  const [impersonating, setImpersonating] = useState(false);

  // Handle View as Patient - Impersonate patient to help them
  const handleViewAsPatient = async (member) => {
    if (impersonating) return;
    
    setImpersonating(true);
    try {
      const response = await axios.post(`${API}/clinic/impersonate-patient`, {
        member_id: member.id,
        reason: 'Admin assisting patient'
      });
      
      if (response.data.success) {
        // Store impersonation data
        localStorage.setItem('patient_impersonation_data', JSON.stringify({
          type: 'patient_impersonation',
          impersonated_patient: response.data.impersonated_patient,
          original_staff_id: response.data.original_staff_id,
          session_id: response.data.session_id,
          expires_at: response.data.expires_at,
          return_url: response.data.return_url
        }));
        
        // Store original user and token for restoration
        localStorage.setItem('original_admin_token', localStorage.getItem('token'));
        localStorage.setItem('original_admin_user', localStorage.getItem('user'));
        
        // Update token and user to impersonated patient
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.impersonated_patient.id,
          email: response.data.impersonated_patient.email,
          full_name: response.data.impersonated_patient.full_name,
          role: response.data.impersonated_patient.role,
          clinic_id: response.data.impersonated_patient.clinic_id
        }));
        
        toast.success(response.data.message);
        
        // Redirect to patient dashboard
        window.location.href = '/patient';
      }
    } catch (error) {
      console.error('Error starting patient view:', error);
      const errorMessage = error.response?.data?.detail || 'Unable to view as patient';
      toast.error(errorMessage);
    } finally {
      setImpersonating(false);
    }
  };

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/plans`);
        setPlans(response.data);
      } catch (error) {
        console.error('Failed to fetch plans');
      }
    };
    fetchPlans();
    
    // Check if clinic has payment gateway configured
    const checkGatewayStatus = async () => {
      try {
        const response = await axios.get(`${API}/clinic-payments/gateway-status`);
        setGatewayStatus(response.data);
      } catch (error) {
        console.error('Failed to check gateway status');
        setGatewayStatus({ has_gateway: false });
      }
    };
    checkGatewayStatus();
  }, []);

  // Advanced Search with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm || filterStatus !== 'all' || filterPlan !== 'all') {
        performSearch();
      } else {
        setSearchResults([]);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, filterStatus, filterPlan]);

  const performSearch = async () => {
    setIsSearching(true);
    try {
      const params = {};
      if (searchTerm) params.q = searchTerm;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterPlan !== 'all') params.plan_id = filterPlan;

      const response = await axios.get(`${API}/search/members`, { params });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterPlan('all');
    setSearchResults([]);
  };

  const handleGetPreview = async () => {
    if (!selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    setLoadingPreview(true);
    try {
      const response = await axios.post(`${API}/members/${selectedMember.id}/membership-preview`, {
        member_id: selectedMember.id,
        plan_id: selectedPlan,
        start_date: startDate || new Date().toISOString()
      });
      setPreview(response.data);
      setShowPreview(true);
    } catch (error) {
      toast.error('Failed to calculate costs');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleConfirmAssignment = async () => {
    // If using gateway payment, redirect to Stripe Checkout
    if (paymentMethod === 'gateway') {
      await handleGatewayPayment();
      return;
    }
    
    // Manual payment methods
    try {
      await axios.post(`${API}/members/${selectedMember.id}/assign-membership`, {
        member_id: selectedMember.id,
        plan_id: selectedPlan,
        start_date: startDate || new Date().toISOString(),
        payment_method: paymentMethod,
        reference_number: referenceNumber,
        payment_notes: paymentNotes
      });
      toast.success('Membership assigned and payment recorded successfully!');
      resetAssignmentDialog();
      fetchData();
    } catch (error) {
      toast.error('Failed to assign membership');
    }
  };

  const handleGatewayPayment = async () => {
    setProcessingGatewayPayment(true);
    try {
      // Create Stripe Checkout session using clinic's gateway
      const response = await axios.post(`${API}/clinic-payments/checkout/membership`, {
        plan_id: selectedPlan,
        member_id: selectedMember.id,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Gateway payment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingGatewayPayment(false);
    }
  };

  const resetAssignmentDialog = () => {
    setDialogOpen(false);
    setSelectedMember(null);
    setSelectedPlan('');
    setStartDate('');
    setPreview(null);
    setShowPreview(false);
    setPaymentMethod('cash');
    setReferenceNumber('');
    setPaymentNotes('');
  };

  const handleBackToSelection = () => {
    setShowPreview(false);
    setPreview(null);
  };

  const handleRenewMembership = async (memberId) => {
    try {
      const response = await axios.post(`${API}/members/${memberId}/renew-membership`);
      toast.success('Membership renewed successfully!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew membership');
    }
  };

  const handleEdit = (member) => {
    setSelectedMember(member);
    setEditFormData({
      phone: member.phone || '',
      date_of_birth: member.date_of_birth || '',
      address: member.address || '',
      emergency_contact: member.emergency_contact || '',
      emergency_phone: member.emergency_phone || ''
    });
    setEditDialogOpen(true);
  };

  // Helper to check if membership date is invalid (epoch date or missing)
  const hasInvalidMembershipDate = (member) => {
    if (!member.membership_end_date) return true;
    try {
      const date = new Date(member.membership_end_date);
      return isNaN(date.getTime()) || date.getFullYear() < 2000;
    } catch {
      return true;
    }
  };

  const handleActivateMembership = async (member) => {
    if (!member.membership_plan_id) {
      toast.error('Member has no assigned plan. Please assign a plan first.');
      return;
    }
    
    if (!window.confirm(`Activate membership for ${member.patient_id}? This will set the membership dates starting from today.`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/members/${member.id}/activate-membership`);
      toast.success(`Membership activated! Valid until ${new Date(response.data.membership_end_date).toLocaleDateString()}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate membership');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use /members/{id} endpoint which allows staff to edit any member
      await axios.put(`${API}/members/${selectedMember.id}`, editFormData);
      toast.success('Member updated successfully');
      setEditDialogOpen(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      console.error('Failed to update member:', error);
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Are you sure you want to delete member ${member.patient_id}? This will only remove the member profile.`)) {
      return;
    }

    try {
      console.log('Deleting member:', member.id);
      const response = await axios.delete(`${API}/members/${member.id}`);
      console.log('Delete response:', response.data);
      toast.success('Member deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    }
  };

  // Complete deletion - removes user account and all data (GDPR compliant)
  const handleDeleteCompletely = async (member) => {
    const confirmed = window.confirm(
      `⚠️ PERMANENT DELETION\n\n` +
      `Are you sure you want to PERMANENTLY delete member ${member.patient_id} (${member.email})?\n\n` +
      `This will delete:\n` +
      `• User account (email/login)\n` +
      `• Member profile\n` +
      `• All memberships\n` +
      `• All appointments\n` +
      `• All payments\n` +
      `• All medical notes\n` +
      `• All messages\n\n` +
      `This action is IRREVERSIBLE!`
    );

    if (!confirmed) return;

    // Double confirmation for safety
    const doubleConfirm = window.confirm(
      `FINAL CONFIRMATION\n\n` +
      `Type of deletion: PERMANENT\n` +
      `Member: ${member.patient_id}\n` +
      `Email: ${member.email}\n\n` +
      `Click OK to permanently delete ALL data.`
    );

    if (!doubleConfirm) return;

    try {
      console.log('Permanently deleting member:', member.id);
      const response = await axios.delete(`${API}/members/${member.id}/complete`);
      console.log('Delete complete response:', response.data);
      toast.success(`Member ${member.patient_id} and all data permanently deleted`);
      fetchData();
    } catch (error) {
      console.error('Delete complete error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    }
  };

  // Create new member manually
  const handleCreateMember = async () => {
    if (!newMemberData.full_name || !newMemberData.email) {
      toast.error('Please fill in required fields (Name and Email)');
      return;
    }

    setCreatingMember(true);
    try {
      // First create user account
      const userResponse = await axios.post(`${API}/members/manual`, {
        full_name: newMemberData.full_name,
        email: newMemberData.email,
        phone: newMemberData.phone,
        date_of_birth: newMemberData.date_of_birth,
        address: newMemberData.address,
        emergency_contact: newMemberData.emergency_contact,
        emergency_phone: newMemberData.emergency_phone,
        plan_id: newMemberData.plan_id || null,
        send_welcome_email: newMemberData.send_welcome_email
      });

      toast.success(`Member ${userResponse.data.patient_id} created successfully!`);
      setAddMemberDialogOpen(false);
      setNewMemberData({
        full_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        address: '',
        emergency_contact: '',
        emergency_phone: '',
        plan_id: '',
        send_welcome_email: true
      });
      fetchData();
    } catch (error) {
      if (error.response?.status === 402) {
        // Member limit reached
        setUpgradeError(error.response.data);
        setUpgradePromptOpen(true);
        setAddMemberDialogOpen(false);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create member');
      }
    } finally {
      setCreatingMember(false);
    }
  };

  // Use search results if searching, otherwise use all members
  const displayMembers = (searchTerm || filterStatus !== 'all' || filterPlan !== 'all') 
    ? searchResults 
    : members;

  return (
    <div data-testid="members-tab">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Members Management</h1>
        <Button onClick={() => setAddMemberDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Member
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>All Members</CardTitle>
                <CardDescription>Search and filter patient members</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Search and Filter Bar */}
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-sm mb-2">Search Members</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, phone, or patient ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-members-input"
                  />
                </div>
              </div>

              {showFilters && (
                <>
                  <div className="w-48">
                    <Label className="text-sm mb-2">Status</Label>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="expired">Expired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-48">
                    <Label className="text-sm mb-2">Plan</Label>
                    <Select value={filterPlan} onValueChange={setFilterPlan}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Plans</SelectItem>
                        {plans.map((plan) => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                  >
                    Clear
                  </Button>
                </>
              )}
            </div>

            {/* Search Status */}
            {isSearching && (
              <div className="text-sm text-gray-500">Searching...</div>
            )}
            {(searchTerm || filterStatus !== 'all' || filterPlan !== 'all') && !isSearching && (
              <div className="text-sm text-gray-600">
                Found {displayMembers.length} member(s)
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Patient ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="text-gray-500">
                      {searchTerm || filterStatus !== 'all' || filterPlan !== 'all' ? 
                        'No members found matching your search criteria.' : 
                        'No members registered yet.'}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                displayMembers.map((member) => (
                  <TableRow key={member.id} data-testid={`member-row-${member.patient_id}`} className="hover:bg-gray-50">
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {member.patient_id}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 hover:bg-teal-50"
                        onClick={() => {
                          setSelectedMember(member);
                          setMemberDetailDialogOpen(true);
                        }}
                        data-testid={`view-btn-${member.patient_id}`}
                        title="Ver información del miembro"
                      >
                        <Eye className="h-4 w-4 text-teal-600" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{member.full_name || 'N/A'}</TableCell>
                  <TableCell className="text-sm text-gray-600">{member.email || 'N/A'}</TableCell>
                  <TableCell>{member.phone || 'N/A'}</TableCell>
                  <TableCell className="text-sm">{member.plan_name || 'No plan'}</TableCell>
                  <TableCell>
                    <Badge variant={member.membership_status === 'active' ? 'default' : 'secondary'}>
                      {member.membership_status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {member.membership_end_date ? new Date(member.membership_end_date).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedMember(member);
                          setDialogOpen(true);
                        }}
                        data-testid={`assign-btn-${member.patient_id}`}
                      >
                        <Award className="h-4 w-4 mr-1" />
                        {member.membership_status === 'active' ? 'Change' : 'Assign'}
                      </Button>
                      {member.membership_plan_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRenewMembership(member.id)}
                          data-testid={`renew-btn-${member.patient_id}`}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Renew
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedMember(member);
                          setNotesDialogOpen(true);
                        }}
                        data-testid={`notes-btn-${member.patient_id}`}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Notes
                      </Button>
                      {member.membership_plan_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                          onClick={() => {
                            setSelectedMember(member);
                            setServiceUsageDialogOpen(true);
                          }}
                          data-testid={`usage-btn-${member.patient_id}`}
                        >
                          <Stethoscope className="h-4 w-4 mr-1" />
                          Usage
                        </Button>
                      )}
                      {/* View as Patient Button */}
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => handleViewAsPatient(member)}
                        disabled={impersonating}
                        data-testid={`view-as-patient-btn-${member.patient_id}`}
                        title="View patient portal as this patient"
                      >
                        <UserCheck className="h-4 w-4 mr-1" />
                        {impersonating ? 'Loading...' : 'View as Patient'}
                      </Button>
                      {member.membership_plan_id && hasInvalidMembershipDate(member) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          onClick={() => handleActivateMembership(member)}
                          data-testid={`activate-btn-${member.patient_id}`}
                          title="Fix membership dates"
                        >
                          <PlayCircle className="h-4 w-4 mr-1" />
                          Activate
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(member)}
                        data-testid={`edit-btn-${member.patient_id}`}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(member)}
                        data-testid={`delete-btn-${member.patient_id}`}
                        title="Delete member profile only"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteCompletely(member)}
                        data-testid={`delete-complete-btn-${member.patient_id}`}
                        title="Permanently delete member and all data (GDPR)"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete All
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Membership Plan</DialogTitle>
            <DialogDescription>
              Assign a membership plan to {selectedMember?.patient_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Plan</Label>
              <select
                className="w-full border rounded-md p-2"
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                data-testid="plan-select"
              >
                <option value="">Choose a plan</option>
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {plan.name} - ${plan.price} ({plan.duration_months} months)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Start Date (Optional)</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date-input"
              />
            </div>

            {!showPreview ? (
              <>
                {/* Payment Method Section */}
                <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                  <h4 className="font-semibold text-gray-900">Payment Details</h4>
                  
                  {/* Gateway Payment Option */}
                  {gatewayStatus?.has_gateway && (
                    <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-teal-800">
                            💳 {gatewayStatus.gateway_name} available
                          </p>
                          <p className="text-sm text-teal-600">
                            Charge directly with credit/debit card
                            {gatewayStatus.test_mode && <span className="ml-1 text-orange-600">(Test mode)</span>}
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          className="border-teal-500 text-teal-700 hover:bg-teal-100"
                          onClick={() => setPaymentMethod('gateway')}
                        >
                          Use {gatewayStatus.gateway_name}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label>Payment Method *</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger data-testid="payment-method-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {gatewayStatus?.has_gateway && (
                          <SelectItem value="gateway">
                            💳 {gatewayStatus.gateway_name} (Online)
                          </SelectItem>
                        )}
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="credit_card">Credit Card (Manual)</SelectItem>
                        <SelectItem value="debit_card">Debit Card (Manual)</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {paymentMethod === 'gateway' && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        ℹ️ By continuing, the payment will be processed through {gatewayStatus?.gateway_name}. 
                        The customer can pay with their credit or debit card.
                      </p>
                    </div>
                  )}
                  
                  {(paymentMethod === 'check' || paymentMethod === 'bank_transfer' || paymentMethod === 'credit_card' || paymentMethod === 'debit_card') && (
                    <div>
                      <Label>Reference Number {paymentMethod === 'check' ? '(Check #)' : ''}</Label>
                      <Input
                        value={referenceNumber}
                        onChange={(e) => setReferenceNumber(e.target.value)}
                        placeholder={paymentMethod === 'check' ? 'Check number' : 'Transaction ID'}
                        data-testid="reference-number-input"
                      />
                    </div>
                  )}
                  
                  {paymentMethod !== 'gateway' && (
                    <div>
                      <Label>Payment Notes (Optional)</Label>
                      <Textarea
                        value={paymentNotes}
                        onChange={(e) => setPaymentNotes(e.target.value)}
                        placeholder="Additional payment information..."
                        rows={2}
                        data-testid="payment-notes-input"
                      />
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleGetPreview}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  disabled={loadingPreview}
                  data-testid="preview-btn"
                >
                  {loadingPreview ? 'Calculating...' : 'Continue to Payment Summary'}
                </Button>
              </>
            ) : (
              <div className="space-y-4">
                {/* Payment Method Summary */}
                <div className="border rounded-lg p-3 bg-gray-50">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">Payment Method:</span>
                    <span className="font-medium capitalize">
                      {paymentMethod === 'gateway' ? `${gatewayStatus?.gateway_name} (Online)` : paymentMethod.replace('_', ' ')}
                    </span>
                  </div>
                  {referenceNumber && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Reference:</span>
                      <span className="font-medium">{referenceNumber}</span>
                    </div>
                  )}
                </div>

                {/* Payment Summary */}
                <div className="border-2 border-teal-500 rounded-lg p-4 bg-teal-50" data-testid="payment-summary">
                  <h3 className="font-bold text-lg text-teal-900 mb-3">Payment Summary</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Patient:</span>
                      <span className="font-medium">{preview?.patient_id}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Plan:</span>
                      <span className="font-medium">{preview?.plan_name}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Duration:</span>
                      <span className="font-medium">{preview?.duration_months} month(s)</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-700">Valid Until:</span>
                      <span className="font-medium">{preview?.end_date ? new Date(preview.end_date).toLocaleDateString() : 'N/A'}</span>
                    </div>
                    
                    <div className="border-t border-teal-300 my-2"></div>
                    
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Membership:</span>
                        <span className="font-medium">${preview?.plan_cost?.toFixed(2)}</span>
                      </div>
                      {preview?.enrollment_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-orange-700">Enrollment Fee:</span>
                          <span className="font-medium text-orange-700">${preview?.enrollment_fee?.toFixed(2)}</span>
                        </div>
                      )}
                      {preview?.is_new_member && (
                        <p className="text-xs text-orange-600 italic">* One-time fee for new members</p>
                      )}
                    </div>
                    
                    <div className="border-t-2 border-teal-600 my-2"></div>
                    
                    <div className="flex justify-between text-lg font-bold">
                      <span className="text-teal-900">TOTAL:</span>
                      <span className="text-teal-900">${preview?.total_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={handleBackToSelection}
                    variant="outline"
                    className="flex-1"
                    data-testid="back-btn"
                    disabled={processingGatewayPayment}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleConfirmAssignment}
                    className={`flex-1 ${paymentMethod === 'gateway' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-teal-600 hover:bg-teal-700'}`}
                    data-testid="confirm-assign-btn"
                    disabled={processingGatewayPayment}
                  >
                    {processingGatewayPayment ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Redirecting to {gatewayStatus?.gateway_name}...
                      </>
                    ) : paymentMethod === 'gateway' ? (
                      <>💳 Pay with {gatewayStatus?.gateway_name}</>
                    ) : (
                      'Confirm & Assign'
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={notesDialogOpen} onOpenChange={setNotesDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Medical Notes - {selectedMember?.patient_id}</DialogTitle>
            <DialogDescription>
              View and add medical notes for this patient
            </DialogDescription>
          </DialogHeader>
          {selectedMember && (
            <MedicalNotesPanel memberId={selectedMember.id} userRole="admin" />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Member - {selectedMember?.patient_id}</DialogTitle>
            <DialogDescription>
              Update member information
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label>Phone Number *</Label>
              <Input
                type="tel"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                required
                data-testid="edit-phone-input"
              />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input
                type="date"
                value={editFormData.date_of_birth}
                onChange={(e) => setEditFormData({...editFormData, date_of_birth: e.target.value})}
                data-testid="edit-dob-input"
              />
            </div>
            <div>
              <AddressInput
                value={editFormData.address}
                onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                label="Address"
                placeholder="Start typing address..."
                data-testid="edit-address-input"
              />
            </div>
            <div>
              <Label>Emergency Contact Name</Label>
              <Input
                value={editFormData.emergency_contact}
                onChange={(e) => setEditFormData({...editFormData, emergency_contact: e.target.value})}
                data-testid="edit-emergency-contact-input"
              />
            </div>
            <div>
              <Label>Emergency Contact Phone</Label>
              <Input
                type="tel"
                value={editFormData.emergency_phone}
                onChange={(e) => setEditFormData({...editFormData, emergency_phone: e.target.value})}
                data-testid="edit-emergency-phone-input"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-teal-600 hover:bg-teal-700"
                data-testid="edit-submit-btn"
              >
                Update Member
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialogOpen} onOpenChange={setAddMemberDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Member</DialogTitle>
            <DialogDescription>
              Create a new member account manually. An email will be sent with login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Full Name *</Label>
                <Input
                  autoComplete="name"
                  value={newMemberData.full_name}
                  onChange={(e) => setNewMemberData({...newMemberData, full_name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <div className="col-span-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  autoComplete="email"
                  value={newMemberData.email}
                  onChange={(e) => setNewMemberData({...newMemberData, email: e.target.value})}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  type="tel"
                  autoComplete="tel"
                  value={newMemberData.phone}
                  onChange={(e) => setNewMemberData({...newMemberData, phone: e.target.value})}
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  autoComplete="bday"
                  value={newMemberData.date_of_birth}
                  onChange={(e) => setNewMemberData({...newMemberData, date_of_birth: e.target.value})}
                />
              </div>
              <div className="col-span-2">
                <AddressInput
                  value={newMemberData.address}
                  onChange={(e) => setNewMemberData({...newMemberData, address: e.target.value})}
                  label="Address"
                  placeholder="Start typing address..."
                  autoComplete="street-address"
                />
              </div>
              <div>
                <Label>Emergency Contact</Label>
                <Input
                  autoComplete="off"
                  value={newMemberData.emergency_contact}
                  onChange={(e) => setNewMemberData({...newMemberData, emergency_contact: e.target.value})}
                  placeholder="Contact name"
                />
              </div>
              <div>
                <Label>Emergency Phone</Label>
                <Input
                  type="tel"
                  autoComplete="off"
                  value={newMemberData.emergency_phone}
                  onChange={(e) => setNewMemberData({...newMemberData, emergency_phone: e.target.value})}
                  placeholder="(555) 987-6543"
                />
              </div>
              <div className="col-span-2">
                <Label>Membership Plan (Optional)</Label>
                <Select 
                  value={newMemberData.plan_id} 
                  onValueChange={(value) => setNewMemberData({...newMemberData, plan_id: value === 'none' ? '' : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a plan (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No plan - Add later</SelectItem>
                    {plans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - ${plan.price}/{plan.duration_months} month(s)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_welcome_email"
                  checked={newMemberData.send_welcome_email}
                  onChange={(e) => setNewMemberData({...newMemberData, send_welcome_email: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="send_welcome_email" className="cursor-pointer">
                  Send welcome email with login credentials
                </Label>
              </div>
            </div>
            <div className="flex gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setAddMemberDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateMember}
                disabled={creatingMember || !newMemberData.full_name || !newMemberData.email}
                className="flex-1 bg-teal-600 hover:bg-teal-700"
              >
                {creatingMember ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Member
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Service Usage Dialog */}
      <MemberServiceUsageDialog
        member={selectedMember}
        isOpen={serviceUsageDialogOpen}
        onClose={() => {
          setServiceUsageDialogOpen(false);
          setSelectedMember(null);
        }}
      />

      {/* Member Detail Dialog */}
      <MemberDetailDialog
        member={selectedMember}
        isOpen={memberDetailDialogOpen}
        onClose={() => {
          setMemberDetailDialogOpen(false);
          setSelectedMember(null);
        }}
      />
    </div>
  );
};


export default MembersTab;
